import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import Select from "react-select";
import { useForm } from "react-hook-form";
import { AxiosError, AxiosResponse } from "axios";
import Icon from "@mdi/react";
import { mdiAccountMultipleOutline, mdiChevronLeft } from "@mdi/js";

import { ModuleEntity, ModulePermissionEntity } from "../../config/defines";
import { SelectOptions, SelectStyle } from "../../config/select";
import { modulePermissionCheck } from "../../config/utils";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { UnitPostRequest, UnitPostResponse, UnitPutRequest, UnitPutResponse } from "../../entities/UnitEntity";
import ClientService from "../../services/ClientService";
import UnitService from "../../services/UnitService";

import Layout from "../../components/Layout";

const toastTitle = "Unidade";

export default function UnidadeFormulario() {
	const unitService = new UnitService();
	const clientService = new ClientService();

	const navigate = useNavigate();
	const { user } = useAuth();
	const { id } = useParams();
	const { handleToast } = useToast();

	const [formStatus, setFormStatus] = useState(id ? false : true);
	const [formSaving, setFormSaving] = useState(false);

	const { data, isLoading, isFetching, isRefetching } = useQuery(["unidade", id], () => fetchData(id), { enabled: !!id && !formStatus });

	const { register, handleSubmit, setValue, reset } = useForm({ defaultValues: data });

	const queryClientes = useQuery(["clientes"], () => fetchDataClientes());

	const mutationPost = useMutation(mutatePost, { onSuccess: mutatePostSuccess, onError: mutateError });
	const mutationPut = useMutation(mutatePut, { onSuccess: mutatePutSuccess, onError: mutateError });

	async function fetchData(id: any) {
		let resp = await unitService.get(id);
		reset(resp.data);
		return resp.data;
	}

	async function fetchDataClientes() {
		let resp = await clientService.getClients();
		return resp.data;
	}

	async function mutatePost(data: UnitPostRequest) {
		setFormSaving(true);
		return await unitService.post(data);
	}

	function mutatePostSuccess(resp: AxiosResponse<UnitPostResponse, any>) {
		handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
		navigate("/usuarioFormulario/" + resp.data.id);
		setFormStatus(false);
		setFormSaving(false);
	}

	async function mutatePut(data: UnitPutRequest) {
		setFormSaving(true);
		return await unitService.put(Number(id), data);
	}

	function mutatePutSuccess(resp: AxiosResponse<UnitPutResponse, any>) {
		handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
		setFormStatus(false);
		setFormSaving(false);
	}

	function mutateError(resp: AxiosError) {
		handleToast(toastTitle, `Problema na operação: ${resp.message}`, 5000, "danger");
		setFormSaving(false);
	}

	async function handleCliente(value: any) {
		setValue(`clientId`, value ? value.value : undefined);
	}

	const formClienteOptions = queryClientes.data?.map((item) => {
		return { label: `${item.name}`, value: item.id };
	});

	return (
		<Layout>
			<Form
				onSubmit={handleSubmit((values) => {
					if (id) {
						mutationPut.mutate({
							name: values.name,
							description: values.description,
						});
					} else {
						mutationPost.mutate({
							name: values.name,
							description: values.description,
							clientId: values.clientId,
						});
					}
				})}
			>
				<h5 className="mt-4 mb-4 d-flex align-items-center fw-light">
					<Link to={"/unidades"} className="d-flex align-items-center text-decoration-none">
						<Icon path={mdiChevronLeft} size={1} className="me-1" /> <Icon path={mdiAccountMultipleOutline} size={1} className="me-1" /> Formulário de Unidade
					</Link>
					{(isLoading || isFetching || isRefetching) && <Spinner size="sm" className="ms-1" variant="secondary" />}
					<div className="d-flex gap-2 float-right ms-auto" style={{ marginTop: -10, marginBottom: -10 }}>
						{modulePermissionCheck(user!, ModuleEntity.acessos, ModulePermissionEntity.editar) && (
							<>
								{!formStatus && (
									<Button
										variant="light"
										className="rounded-3 shadow-sm"
										onClick={() => {
											setFormStatus(true);
										}}
									>
										Editar Informações
									</Button>
								)}
								{formStatus && (
									<>
										<Button
											variant="light"
											className="rounded-3 shadow-sm"
											type="button"
											onClick={() => {
												if (id) {
													setFormStatus(false);
												} else {
													navigate("/unidades");
												}
											}}
											disabled={formSaving}
										>
											Cancelar
										</Button>
										<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
											{formSaving ? (
												<>
													<Spinner animation="border" size="sm" className="me-2" /> Salvando
												</>
											) : (
												"Salvar Informações"
											)}
										</Button>
									</>
								)}
							</>
						)}
					</div>
				</h5>

				<Card className="mb-3">
					<Card.Body>
						<Form.Group className="mb-3" controlId="nome">
							<Form.Label>Nome</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui" {...register("name", { required: true })} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="description">
							<Form.Label>Descrição</Form.Label>
							<Form.Control type="email" placeholder="Informe aqui" {...register("description", { required: true })} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="client">
							<Form.Label>Cliente</Form.Label>
							{(!queryClientes.isFetched || isLoading) && (
								<div className="form-control disabled">
									<Spinner size="sm" />
								</div>
							)}
							{queryClientes.isFetched && !isLoading && (
								<Select
									placeholder={"Selecione"}
									defaultValue={formClienteOptions?.find((option) => {
										return option.value === data?.clientId;
									})}
									options={formClienteOptions}
									onChange={(value) => {
										handleCliente(value);
									}}
									styles={SelectStyle}
									isDisabled={!formStatus || !!id}
									{...SelectOptions}
								/>
							)}
						</Form.Group>
					</Card.Body>
				</Card>

				{modulePermissionCheck(user!, ModuleEntity.acessos, ModulePermissionEntity.editar) && (
					<div className="d-flex gap-2 mb-4">
						{!formStatus && (
							<Button
								variant="light"
								className="rounded-3 shadow-sm"
								onClick={() => {
									setFormStatus(true);
								}}
							>
								Editar Informações
							</Button>
						)}
						{formStatus && (
							<>
								<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
									{formSaving ? (
										<>
											<Spinner animation="border" size="sm" className="me-2" /> Salvando
										</>
									) : (
										"Salvar Informações"
									)}
								</Button>
								<Button
									variant="light"
									className="rounded-3 shadow-sm"
									type="button"
									onClick={() => {
										if (id) {
											setFormStatus(false);
										} else {
											navigate("/unidades");
										}
									}}
									disabled={formSaving}
								>
									Cancelar
								</Button>
							</>
						)}
					</div>
				)}
			</Form>
		</Layout>
	);
}
